import { OwFForm } from "@faire/retailer-visitor-shared/components/OpenWithFaire/OpenWithFaireApplication/OwFForm";
import { useEffectOwFApplicationEligibilityRedirect } from "@faire/retailer-visitor-shared/components/OpenWithFaire/OpenWithFaireApplication/useEffectOwFApplicationEligibilityRedirect";
import { getDefaultStoreOpeningDate } from "@faire/retailer-visitor-shared/components/OpenWithFaire/OpenWithFaireApplication/utils";
import { useRetailer } from "@faire/retailer-visitor-shared/hooks/retailer/useRetailer";
import { useRetailerStatus } from "@faire/retailer-visitor-shared/hooks/retailer/useRetailerStatus";
import { useOpenWithFaireEntrypoints } from "@faire/retailer-visitor-shared/stores/domain/OpenWithFaire/useOpenWithFaireEntrypoints";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { trackEvent } from "@faire/web--source/common/trackEvent";
import { Column } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { useQueryParams } from "@faire/web--source/ui/hooks/useQueryParams";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { trackOpenWithFaireSignupApplicationShown } from "@faire/web-api--source/events/openWithFaire/actionCategoryOther/signupApplicationShown";
import { IRetailer } from "@faire/web-api--source/indigofair/data/IRetailer";
import { IRetailerLaunchWithFaireApplication } from "@faire/web-api--source/indigofair/data/IRetailerLaunchWithFaireApplication";
import { QueryParameters as OWFQueryParams } from "@faire/web-api--source/routes/www/welcome/r/open-with-faire/application";
import React, { useEffect } from "react";
import styled from "styled-components";

import { useRetailerOpenWithFaire } from "./useRetailerOpenWithFaire";

const { LeaseSigned } = IRetailerLaunchWithFaireApplication;

interface IProps {
  onSubmit: () => void;
  onSkip: () => void;
}

export const OpenWithFaireApplicationForm: React.FC<IProps> = ({
  onSkip,
  onSubmit,
}) => {
  const { strictLocalize } = useStrictLocalization();
  const {
    leaseSigned: leaseSignedQueryParam,
    storeOpeningDate: storeOpeningDateQueryParam,
  } = useQueryParams(OWFQueryParams.parse);
  const { reportedCreationTime } = useRetailerStatus();
  const { retailer } = useRetailer();
  const { openWithFaire } = useRetailerOpenWithFaire();
  const { isTreatmentVisible } = useOpenWithFaireEntrypoints();
  const leaseSigned =
    leaseSignedQueryParam === LeaseSigned.YES ||
    leaseSignedQueryParam === LeaseSigned.OWNED ||
    leaseSignedQueryParam === LeaseSigned.NO
      ? leaseSignedQueryParam
      : undefined;
  const openingDate =
    storeOpeningDateQueryParam ?? openWithFaire?.store_opening_month;
  const storeOpeningDate = parseInt((openingDate ?? "").toString(), 10);

  const defaultStoreOpeningDate = getDefaultStoreOpeningDate(
    strictLocalize,
    isNaN(storeOpeningDate) ? undefined : storeOpeningDate,
    reportedCreationTime
  );

  useEffectOwFApplicationEligibilityRedirect();

  useEffect(() => {
    if (
      !isTreatmentVisible ||
      (retailer?.business_category ===
        IRetailer.BusinessCategory.BRICK_AND_MORTAR &&
        leaseSigned === LeaseSigned.NO)
    ) {
      // Queryparam is not complete, we are missing two mandatory fields
      //   skip application for now so that Retailer can apply later on
      onSkip();
      return;
    }
    trackOpenWithFaireSignupApplicationShown();
  }, [leaseSigned, retailer, onSkip, isTreatmentVisible]);

  const handleSkip = () => {
    trackEvent("/owf-signup/application/skip");
    onSkip();
  };

  return (
    <Column align="flex-start">
      <Header />
      <Spacer height="4x" />
      <OwFForm
        afterSubmit={onSubmit}
        onSkip={handleSkip}
        isSignUp
        startingValues={{
          lease_signed: leaseSigned,
          opening_range: defaultStoreOpeningDate,
        }}
      />
    </Column>
  );
};

const Header: React.FC = () => {
  return (
    <>
      <TagLine variant="paragraphSansMedium">
        <StrictLocalMsg
          defaultMessage="Open with Faire"
          description={{
            text: "Name of a program that Faire offers to new retailers",
          }}
        />
      </TagLine>
      <Spacer height="2x" />
      <Title variant="displaySSerifRegular">
        <StrictLocalMsg
          defaultMessage="Tell us about your new business"
          description={{ text: "Title of an application form" }}
        />
      </Title>
      <Spacer height="2x" />
      <Description>
        <StrictLocalMsg
          defaultMessage="We’ll use this info to determine your new 60-day payment terms on Faire."
          description={{
            text: "Describing what is this application used for",
          }}
        />
      </Description>
    </>
  );
};

const Description = styled(Typography)`
  &&& {
    max-width: 350px;
  }
`;

const TagLine = styled(Typography)`
  &&& {
    letter-spacing: 1px;
  }
`;

const Title = styled(Typography)`
  &&& {
    max-width: 433px;
  }
`;
